import { useState } from "react";

import { formatShortWeekdayDate } from "@shared";

import { DutyScheduleColumnResponse, DutyScheduleCounselorOptionResponse } from "@/api";
import { DutyScheduleAssignmentCell } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/assignment-table/DutyScheduleAssignmentCell";
import { DutyScheduleCounselorPicker } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/counselor-picker/DutyScheduleCounselorPicker";
import {
  DutyScheduleTableColumn,
  DutyScheduleTableLayout,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout";
import { DutyScheduleCell } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";
import { useTranslation } from "@/translations";

const DATE_COLUMN_WIDTH = 150;
// Every duty column keeps the same width whatever its label, so the codes line up in a grid instead
// of each column shrinking to its own content.
const DUTY_COLUMN_WIDTH = 120;

interface OpenCell {
  date: Date;
  column: DutyScheduleColumnResponse;
  anchorEl: HTMLElement;
}

interface Props {
  workingDays: Date[];
  columns: DutyScheduleColumnResponse[];
  isEditable: boolean;
  counselors: DutyScheduleCounselorOptionResponse[];
  // The counselor ids the table is filtered to, or null for all of them.
  counselorFilter: string[] | null;
  resolveCell: (date: Date, columnId: string) => DutyScheduleCell | undefined;
  onAssign: (date: Date, columnId: string, counselorId: string | null) => void;
}

export function DutyScheduleAssignmentTable({
  workingDays,
  columns,
  isEditable,
  counselors,
  counselorFilter,
  resolveCell,
  onAssign,
}: Props) {
  const { t } = useTranslation();
  const [openCell, setOpenCell] = useState<OpenCell | null>(null);

  const openCellValue = openCell == null ? undefined : resolveCell(openCell.date, openCell.column.id);

  // One table column per duty column, its `renderCell`/`getHighlight` closing over that duty column
  // so both can key off (date, column) even though the shell only threads the row (date) through.
  // `isEditable` is constant for the whole table, so its p:0 (the ButtonBase fills the cell itself)
  // is static per-column rather than something `getHighlight` needs to express.
  const tableColumns: Array<DutyScheduleTableColumn<Date>> = columns.map((column) => ({
    field: column.id,
    headerName: column.label,
    width: DUTY_COLUMN_WIDTH,
    cellSx: isEditable ? { whiteSpace: "nowrap", p: 0 } : { whiteSpace: "nowrap" },
    getHighlight: (date) => {
      const cell = resolveCell(date, column.id);
      if (cell == null) {
        return "inactive";
      }
      return counselorFilter != null && cell.counselorId != null && counselorFilter.includes(cell.counselorId)
        ? "match"
        : "none";
    },
    renderCell: (date) => {
      const cell = resolveCell(date, column.id);
      const isHighlighted =
        counselorFilter != null && cell?.counselorId != null && counselorFilter.includes(cell.counselorId);
      const isOpen = openCell?.column.id === column.id && openCell.date.getTime() === date.getTime();

      return (
        <DutyScheduleAssignmentCell
          cell={cell}
          date={date}
          dutyLabel={column.label}
          isEditable={isEditable}
          isHighlighted={isHighlighted}
          isOpen={isOpen}
          onOpen={(anchorEl) => setOpenCell({ date, column, anchorEl })}
        />
      );
    },
  }));

  const dateColumn: DutyScheduleTableColumn<Date> = {
    field: "date",
    headerName: t("dutySchedule.detail.dataEntry.dateColumn"),
    width: DATE_COLUMN_WIDTH,
    bold: true,
    renderCell: formatShortWeekdayDate,
  };

  return (
    <>
      <DutyScheduleTableLayout
        ariaLabel={t("dutySchedule.detail.dataEntry.tableAria")}
        columns={[dateColumn, ...tableColumns]}
        data={workingDays}
        getRowId={(date) => date.getTime().toString()}
      />

      {openCell != null && (
        <DutyScheduleCounselorPicker
          anchorEl={openCell.anchorEl}
          dutyLabel={openCell.column.label}
          columnKind={openCell.column.kind}
          columnCourtId={openCell.column.courtId}
          date={openCell.date}
          assignedCounselorId={openCellValue?.counselorId ?? null}
          counselors={counselors}
          onSelect={(counselorId) => {
            onAssign(openCell.date, openCell.column.id, counselorId);
            setOpenCell(null);
          }}
          onClose={() => setOpenCell(null)}
        />
      )}
    </>
  );
}
